/**
 * What the last Map visit already did (B-28, B-53).
 *
 * MapPage unmounts on every tab switch, so nothing it holds survives a visit.
 * Every visit therefore re-wrote the location and re-ran both discovery
 * searches: 8 visits in 13 s were 8 location writes and 16 searches. The
 * searches share a 60-per-15-minutes budget with Listings, so a trader hopping
 * between chat and the map ran it dry on the 31st visit and Listings went down
 * with it. Module state outlives the page, so it is kept here.
 *
 * - Everything here is per account: MapPage passes `user.id`, the signed-in
 *   account's `_id`. Signing out does not reload the app, so this state can
 *   outlive one account and must never answer for the next one.
 * - The location is written again only after a move of more than MOVE_M, or
 *   for a different account. That record does not expire: the server derives
 *   the public pin from the stored point alone (no time in it), so rewriting an
 *   unmoved point changes nothing. It lasts until the app process ends, so
 *   another device moving the same account elsewhere is not undone from here
 *   until this one moves.
 * - A visit within FRESH_MS of a successful search, from the same spot, reuses
 *   its answers instead of asking again. A failed search is never kept, so the
 *   next visit asks.
 * - Changing your own wishlist, collection, trades, blocks or map privacy can
 *   change your matches, so any successful write to those forgets the answers.
 *   A search that was already out when such a write landed may have been
 *   answered before it, so its answers are not kept either: each search takes
 *   a ticket before it goes out, and a write in between voids the ticket.
 * - Collector hotspots (asked for only when nobody is nearby) are on the same
 *   budget and take no position, so they are reused for FRESH_MS too.
 */
import axios from 'axios';

const MOVE_M = 250;
const FRESH_MS = 3 * 60 * 1000;

let written = null;
let found = null;
let spots = null;
// Bumped by every write that can change matches.
let changes = 0;

// Flat-earth metres: exact enough at a 250 m scale.
const metres = (a, lng, lat) => {
  const k = Math.PI / 180;
  const x = (lng - a.lng) * Math.cos(((lat + a.lat) / 2) * k);
  return Math.hypot(x, lat - a.lat) * k * 6371e3;
};

const near = (a, userId, lng, lat) =>
  !!a && a.userId === userId && metres(a, lng, lat) <= MOVE_M;

export const needsLocationWrite = (userId, lng, lat) => !near(written, userId, lng, lat);

export const noteLocationWritten = (userId, lng, lat) => {
  written = { userId, lng, lat };
};

/** The last successful search's answers, if they still describe this visit. */
export const recentDiscovery = (userId, lng, lat, radius, now = Date.now()) =>
  near(found, userId, lng, lat) && found.radius === radius && now - found.at < FRESH_MS
    ? found
    : null;

/** Take one before a search goes out, and hand it to rememberDiscovery. */
export const discoveryTicket = () => changes;

export const rememberDiscovery = (ticket, userId, lng, lat, radius, users, matches, now = Date.now()) => {
  if (ticket === changes) found = { userId, lng, lat, radius, users, matches, at: now };
};

export const recentHotspots = (userId, now = Date.now()) =>
  spots && spots.userId === userId && now - spots.at < FRESH_MS ? spots.rows : null;

export const rememberHotspots = (userId, rows, now = Date.now()) => {
  spots = { userId, rows, at: now };
};

/** Test seam: every test starts with no remembered visit. */
export const resetDiscoveryVisitCache = () => {
  written = null;
  found = null;
  spots = null;
};

const CHANGES_MATCHES = /\/(wishlist|collection|trades|trust\/block|users\/location-privacy)\b/;

axios?.interceptors?.response?.use?.((res) => {
  const { method, url } = res?.config || {};
  if (method && method !== 'get' && CHANGES_MATCHES.test(url || '')) {
    found = null;
    spots = null;
    changes += 1;
  }
  return res;
});
